/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { Ref, RefObject } from 'preact';
import type { UnsafeDomHandle } from '#utils/UNSAFE_unsafeDomAccess';
import type { Column, Columns, ColumnWidths, TableViewportConfig } from '#UNSAFE_TableView';
import type { CollectionData } from '#PRIVATE_TableCommon/Common.types';
import type { Columns as DisplayColumns } from '#PRIVATE_TableDisplay';
import type {
  AppliedColumnWidths,
  BorderEdges,
  OverflowInfo,
  SampledWidths,
  SizingInfo,
  StickyColumnInfo
} from '../Table';

import { createRef } from 'preact';
import {
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'preact/hooks';
import { useResizeObserver } from '#hooks/UNSAFE_useResizeObserver';
import { UNSAFE_DOM_ACCESS } from '#utils/UNSAFE_unsafeDomAccess';
import { useAddBusyState } from '#hooks/PRIVATE_useAddBusyState';
import { isHTMLScroller } from '#PRIVATE_TableCommon/utils/ScrollUtils';
import { applyLayoutColumnWidths, columnWidthsComparator } from '../utils/TableLayoutUtils';
import {
  applyOverflowStatus,
  applyUnderflowStatus,
  getStickyColumnIndicies,
  getViewportConfig,
  updateStickyColumns,
  updateStickyEdges
} from '../utils/TableScrollUtils';

type UseSizingManagerProps<K, D, C extends string> = {
  rootRef: RefObject<HTMLDivElement>;
  innerTableRef: RefObject<HTMLTableElement>;
  tableId: string;
  tableData: CollectionData<K, D>[];
  viewportConfig?: TableViewportConfig;
  externalScrollerAccessRef?: Ref<UnsafeDomHandle>;
  layout?: 'contents' | 'fixed' | 'pending';
  columns: Columns<K, D, C> | DisplayColumns<K, D, C>;
  columnOrder?: C[];
  columnWidths?: ColumnWidths<C>;
  isRtl: boolean;
  isHighContrast: boolean;
};

/**
 * Hook that manages column sizing on the Preact Table
 */
export function useSizingManager<K extends string | number, D, C extends string>({
  rootRef,
  innerTableRef,
  tableId,
  tableData,
  viewportConfig,
  externalScrollerAccessRef,
  layout,
  columns,
  columnOrder,
  columnWidths,
  isRtl,
  isHighContrast
}: UseSizingManagerProps<K, D, C>) {
  // scrollerRef is callback-based to meet useResizeObserver hook's state management requirements
  const [scrollerRef, setScrollerRef] = useState(createRef<HTMLElement>());
  const scrollerRefCallback = useCallback((node: HTMLDivElement | null) => {
    setScrollerRef({ current: node });
  }, []);

  useImperativeHandle(
    externalScrollerAccessRef!,
    () => {
      if (viewportConfig != null) {
        return { [UNSAFE_DOM_ACCESS]: viewportConfig.scrollerRef.current! };
      }
      return { [UNSAFE_DOM_ACCESS]: scrollerRef.current! };
    },
    [scrollerRef, viewportConfig]
  );

  // setup busy start and busy end callbacks for the 2 resize observer regions
  const { onBusyStart: onResizeScrollerBusyStart, onBusyEnd: onResizeScrollerBusyEnd } =
    useAddBusyState('handling scroller resize');
  const { onBusyStart: onResizeTableBusyStart, onBusyEnd: onResizeTableBusyEnd } =
    useAddBusyState('handling table resize');

  // state to force a rerender following the initial render where scrollbars are forced
  const [_isScrollbarSizeKnown, setIsScrollbarSizeKnown] = useState<boolean>(false);

  // column width state information
  const sampledColumnNetWidthsRef = useRef<SampledWidths<C>>();
  const [appliedColumnWidths, setAppliedColumnWidths] = useState<
    AppliedColumnWidths<C> | undefined
  >();

  const [innerTableHeight, setInnerTableHeight] = useState<number | undefined>();
  const [hasVerticalUnderflow, setHasVerticalUnderflow] = useState<boolean>(false);

  // general table sizing state information
  const sizingInfoRef = useRef<SizingInfo>({
    isInitialRender: true,
    isScrollbarSizeKnown: false
  });
  const pendingResizeEntriesRef = useRef<{
    scroller?: ResizeObserverEntry | undefined;
    innerTable?: ResizeObserverEntry | undefined;
  }>({});

  // scrollbar overflow state
  const overflowInfoRef = useRef<OverflowInfo>({
    isHorizontal: false,
    isVertical: false
  });

  // sticky edge state management
  const [stickyEdgesState, setStickyEdgesState] = useState<{ start?: number; end?: number }>({});

  // current viewport information
  const viewportInfo = useMemo(() => {
    const tableViewportConfig = getViewportConfig(scrollerRef, viewportConfig);
    return {
      isExternalScroller: viewportConfig != null,
      viewportConfig: tableViewportConfig
    };
  }, [scrollerRef, viewportConfig]);

  // memoize realized columns array on 'columns' and 'columnOrder'
  const columnsArray = useMemo(() => {
    const newColumnsArray = [];
    if (columnOrder != null) {
      for (const key of columnOrder) {
        if (columns[key] != null) {
          newColumnsArray.push({
            key: key,
            value: columns[key]
          });
        }
      }
    } else {
      for (const [key, value] of Object.entries(columns)) {
        newColumnsArray.push({
          key: key as C,
          value: value as Column<K, D, C>
        });
      }
    }
    return newColumnsArray;
  }, [columnOrder, columns]);

  const stickyColumnInfo: StickyColumnInfo = useMemo(() => {
    const stickyColumns = getStickyColumnIndicies(columnsArray);

    let firstStickyColumnIndex: number | undefined;
    let finalStickyColumnIndex: number | undefined;
    const stickyColumnsCount = stickyColumns.length;
    if (stickyColumns.length > 0) {
      firstStickyColumnIndex = stickyColumns[0];
      finalStickyColumnIndex = stickyColumns[stickyColumnsCount - 1];
    }
    return { stickyColumns, firstStickyColumnIndex, finalStickyColumnIndex };
  }, [columnsArray]);

  const borderOffset = isHighContrast ? 3 : 1;
  const columnBorderEdges: BorderEdges = useMemo(() => {
    const newColumnBorderEdges: BorderEdges = [];
    let isPreviousColumnHasEnd = false;
    for (let i = 0; i < columnsArray.length; i++) {
      const hasStart = !isPreviousColumnHasEnd;
      const isLastColumn = i === columnsArray.length - 1;
      const isColumnSticky = stickyColumnInfo.stickyColumns.includes(i);
      const isLastStickyColumn = isColumnSticky && stickyColumnInfo.finalStickyColumnIndex === i;
      const hasEnd = isLastColumn || isLastStickyColumn || stickyEdgesState?.start === i;
      const width = (hasStart ? borderOffset : 0) + (hasEnd ? borderOffset : 0);
      let offset;
      if (
        isLastStickyColumn ||
        (isLastColumn && stickyColumnInfo.finalStickyColumnIndex !== i - 1)
      ) {
        offset = 2 * borderOffset;
      } else {
        offset =
          !isLastColumn && stickyColumnInfo.finalStickyColumnIndex === i - 1 ? 0 : borderOffset;
      }
      newColumnBorderEdges.push({ hasStart, hasEnd, width, offset });
      isPreviousColumnHasEnd = hasEnd;
    }
    return newColumnBorderEdges;
  }, [borderOffset, columnsArray, stickyEdgesState, stickyColumnInfo]);

  // determine if a new layout is required
  let isLayoutRequired = false;
  const netWidthsArray = [];
  if (appliedColumnWidths != null) {
    for (let i = 0; i < columnsArray.length; i++) {
      const key = columnsArray[i].key;
      const netWidth = appliedColumnWidths.netColumnWidths[key];
      const staticWidth = columnWidths != null ? columnWidths[key] : undefined;
      if (netWidth == null || (staticWidth != null && staticWidth !== netWidth)) {
        isLayoutRequired = true;
        break;
      }
      netWidthsArray.push(netWidth);
    }
  } else {
    isLayoutRequired = true;
  }

  // ensure sampled column sizes are cleared if 'columns', 'columnOrder', or 'layout' is updated
  useLayoutEffect(() => {
    sampledColumnNetWidthsRef.current = undefined;
    setAppliedColumnWidths(undefined);
  }, [columns, columnOrder, layout]);

  // ensure applied column widths are cleared if 'columnWidths' is updated
  useLayoutEffect(() => {
    setAppliedColumnWidths(undefined);
  }, [columnWidths, stickyEdgesState]);

  // setup sizing calculation updates for subsequent renders
  useLayoutEffect(() => {
    const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
    const innerTableElement = innerTableRef.current;
    if (
      layout !== 'pending' &&
      scrollerElement != null &&
      innerTableElement != null &&
      !sizingInfoRef.current.isInitialRender &&
      (viewportInfo.isExternalScroller || sizingInfoRef.current.isScrollbarSizeKnown) &&
      isLayoutRequired &&
      columnsArray.length > 0
    ) {
      let widthOffset = 0;
      if (viewportInfo.isExternalScroller) {
        const scrollerOffsetStart = viewportInfo.viewportConfig.scrollerOffsetStart;
        if (scrollerOffsetStart != null) {
          widthOffset += scrollerOffsetStart;
        }
        const scrollerOffsetEnd = viewportInfo.viewportConfig.scrollerOffsetEnd;
        if (scrollerOffsetEnd != null) {
          widthOffset += scrollerOffsetEnd;
        }
      } else if (sizingInfoRef.current.isScrollbarSizeKnown) {
        widthOffset = sizingInfoRef.current.defaultScrollbarWidth;
      }
      const totalWidth = layout === 'fixed' ? sizingInfoRef.current.contentWidth - widthOffset : -1;
      const newColumnWidths = applyLayoutColumnWidths(
        scrollerElement,
        viewportInfo,
        innerTableElement,
        widthOffset,
        tableId,
        layout === 'fixed',
        sampledColumnNetWidthsRef,
        totalWidth,
        columnsArray,
        columnBorderEdges,
        borderOffset,
        columnWidths
      );
      setAppliedColumnWidths(newColumnWidths);
    }
  }, [
    viewportInfo,
    innerTableRef,
    tableId,
    columnsArray,
    columnBorderEdges,
    borderOffset,
    columnWidths,
    layout,
    isLayoutRequired,
    isHighContrast
  ]);

  useLayoutEffect(() => {
    const rootElement = rootRef.current;
    const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
    if (
      layout !== 'pending' &&
      rootElement != null &&
      scrollerElement != null &&
      appliedColumnWidths != null
    ) {
      updateStickyColumns(
        rootElement,
        scrollerElement,
        viewportInfo,
        tableId,
        isRtl,
        columnsArray,
        stickyColumnInfo,
        sizingInfoRef.current,
        overflowInfoRef.current,
        appliedColumnWidths.fullColumnWidths,
        stickyEdgesState,
        setStickyEdgesState,
        viewportInfo.isExternalScroller || overflowInfoRef.current.isHorizontal
      );
    }
  }, [
    tableData,
    rootRef,
    viewportInfo,
    tableId,
    appliedColumnWidths,
    columnsArray,
    stickyColumnInfo,
    layout,
    stickyEdgesState,
    isRtl
  ]);

  // helper method to update the sticky edge styling due to resize events
  const _updateStickyEdgesFromResize = useCallback(
    (rootElement: HTMLDivElement, scrollerElement: HTMLElement) => {
      if (appliedColumnWidths != null) {
        const sizingInfo = sizingInfoRef.current;
        const overflowInfo = overflowInfoRef.current;
        if (viewportInfo.isExternalScroller || overflowInfo.isHorizontal) {
          // ensure sticky edges are applied correctly if horizontal overflow is present
          updateStickyEdges(
            rootElement,
            scrollerElement,
            viewportInfo,
            columnsArray,
            stickyColumnInfo,
            sizingInfo,
            overflowInfo,
            appliedColumnWidths.fullColumnWidths,
            isRtl,
            stickyEdgesState,
            setStickyEdgesState
          );
        } else if (stickyEdgesState.start != null || stickyEdgesState.end != null) {
          // otherwise clear sticky edges if necessary
          setStickyEdgesState({});
        }
      }
    },
    [appliedColumnWidths, columnsArray, stickyColumnInfo, stickyEdgesState, viewportInfo, isRtl]
  );

  // callback function to handle resizes of the scroller and inner table elements
  const handleResizeCallback = useCallback(() => {
    let currentEntry = pendingResizeEntriesRef.current.scroller;
    const rootElement = rootRef.current;
    const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
    const innerTableElement = innerTableRef.current;
    let isOveralSizeHandled = false;

    // handle scroller element resize notification
    if (
      currentEntry != null &&
      rootElement != null &&
      scrollerElement != null &&
      innerTableElement != null
    ) {
      const newContentWidth = currentEntry.contentBoxSize[0].inlineSize;
      const newContentHeight = currentEntry.contentBoxSize[0].blockSize;
      let newBoxWidth;
      let newBoxHeight;
      // for HTML scroller, innerWidth / innerHeight to ensure scrollbar size is included
      if (isHTMLScroller(scrollerElement)) {
        newBoxWidth = window.innerWidth;
        newBoxHeight = window.innerHeight;
      } else {
        newBoxWidth = currentEntry.borderBoxSize[0].inlineSize;
        newBoxHeight = currentEntry.borderBoxSize[0].blockSize;
      }

      if (newBoxWidth !== 0 || newBoxHeight !== 0) {
        isOveralSizeHandled = true;
        let availableWidth;
        let availableWidthOffset = 0;
        let defaultScrollbarWidth = 0;
        if (viewportInfo.isExternalScroller) {
          const scrollerOffsetStart = viewportInfo.viewportConfig.scrollerOffsetStart;
          if (scrollerOffsetStart != null) {
            availableWidthOffset += scrollerOffsetStart;
          }
          const scrollerOffsetEnd = viewportInfo.viewportConfig.scrollerOffsetEnd;
          if (scrollerOffsetEnd != null) {
            availableWidthOffset += scrollerOffsetEnd;
          }
          if (sizingInfoRef.current.isScrollbarSizeKnown) {
            sizingInfoRef.current = {
              isInitialRender: false,
              contentWidth: newContentWidth,
              boxWidth: newBoxWidth,
              contentHeight: newContentHeight,
              boxHeight: newBoxHeight,
              isScrollbarSizeKnown: true,
              defaultScrollbarWidth: sizingInfoRef.current.defaultScrollbarWidth,
              defaultScrollbarHeight: sizingInfoRef.current.defaultScrollbarHeight
            };
          } else {
            sizingInfoRef.current = {
              isInitialRender: false,
              contentWidth: newContentWidth,
              boxWidth: newBoxWidth,
              contentHeight: newContentHeight,
              boxHeight: newBoxHeight,
              isScrollbarSizeKnown: false
            };
          }
          availableWidth = newContentWidth - availableWidthOffset;
        } else {
          const isInitialRender = sizingInfoRef.current.isInitialRender;
          if (sizingInfoRef.current.isScrollbarSizeKnown) {
            availableWidthOffset = sizingInfoRef.current.defaultScrollbarWidth;
            sizingInfoRef.current = {
              isInitialRender: false,
              contentWidth: newContentWidth,
              boxWidth: newBoxWidth,
              contentHeight: newContentHeight,
              boxHeight: newBoxHeight,
              isScrollbarSizeKnown: true,
              defaultScrollbarWidth: sizingInfoRef.current.defaultScrollbarWidth,
              defaultScrollbarHeight: sizingInfoRef.current.defaultScrollbarHeight
            };
          } else {
            // on the first render that does not specify an external scroller, horizontal and
            // vertical scrollbars are forced in order to determine default browser sizes
            availableWidthOffset = newBoxWidth - newContentWidth;
            sizingInfoRef.current = {
              isInitialRender: false,
              contentWidth: newContentWidth,
              boxWidth: newBoxWidth,
              contentHeight: newContentHeight,
              boxHeight: newBoxHeight,
              isScrollbarSizeKnown: true,
              defaultScrollbarWidth: availableWidthOffset,
              defaultScrollbarHeight: newBoxHeight - newContentHeight
            };
            setIsScrollbarSizeKnown(true);
          }
          defaultScrollbarWidth = sizingInfoRef.current.defaultScrollbarWidth;
          availableWidth = !isInitialRender || layout === 'fixed' ? newContentWidth : -1;
        }
        if (columnsArray.length > 0) {
          let newColumnWidths;
          if (layout !== 'pending') {
            newColumnWidths = applyLayoutColumnWidths(
              scrollerElement,
              viewportInfo,
              innerTableElement,
              availableWidthOffset,
              tableId,
              layout === 'fixed',
              sampledColumnNetWidthsRef,
              availableWidth,
              columnsArray,
              columnBorderEdges,
              borderOffset,
              columnWidths
            );
            if (
              appliedColumnWidths == null ||
              !columnWidthsComparator(
                appliedColumnWidths.netColumnWidths,
                newColumnWidths.netColumnWidths
              )
            ) {
              setAppliedColumnWidths(newColumnWidths);
            } else {
              newColumnWidths = undefined;
            }
          }
          // ensure scrollbars are applied correctly - a scrollbar could be needed
          if (viewportInfo.isExternalScroller) {
            applyUnderflowStatus(rootElement, tableId, setHasVerticalUnderflow);
          } else {
            const innerTableRect = innerTableElement.getBoundingClientRect();
            applyOverflowStatus(
              rootElement,
              scrollerElement,
              tableId,
              innerTableRect.width,
              newContentWidth,
              innerTableRect.height,
              newContentHeight,
              defaultScrollbarWidth,
              overflowInfoRef,
              setHasVerticalUnderflow
            );
          }
          if (layout !== 'pending') {
            if (newColumnWidths != null) {
              updateStickyColumns(
                rootElement,
                scrollerElement,
                viewportInfo,
                tableId,
                isRtl,
                columnsArray,
                stickyColumnInfo,
                sizingInfoRef.current,
                overflowInfoRef.current,
                newColumnWidths.fullColumnWidths,
                stickyEdgesState,
                setStickyEdgesState,
                viewportInfo.isExternalScroller || overflowInfoRef.current.isHorizontal
              );
            } else {
              _updateStickyEdgesFromResize(rootElement, scrollerElement);
            }
          }
        }
      }
    }
    currentEntry = pendingResizeEntriesRef.current.innerTable;
    if (currentEntry != null) {
      const newBoxWidth = currentEntry.borderBoxSize[0].inlineSize;
      const newBoxHeight = currentEntry.borderBoxSize[0].blockSize;
      // if only an inner table resize occurred, ensure scrollbars are applied correctly
      if (
        !isOveralSizeHandled &&
        rootElement != null &&
        scrollerElement != null &&
        columnsArray.length > 0 &&
        (newBoxWidth !== 0 || newBoxHeight !== 0)
      ) {
        if (viewportInfo.isExternalScroller) {
          applyUnderflowStatus(rootElement, tableId, setHasVerticalUnderflow);
        } else if (sizingInfoRef.current.isScrollbarSizeKnown) {
          applyOverflowStatus(
            rootElement,
            scrollerElement,
            tableId,
            newBoxWidth,
            sizingInfoRef.current.contentWidth,
            newBoxHeight,
            sizingInfoRef.current.contentHeight,
            sizingInfoRef.current.defaultScrollbarWidth,
            overflowInfoRef,
            setHasVerticalUnderflow
          );
        }
        if (layout !== 'pending') {
          _updateStickyEdgesFromResize(rootElement, scrollerElement);
        }
      }
      setInnerTableHeight(newBoxHeight);
    }
    pendingResizeEntriesRef.current = {};
  }, [
    rootRef,
    viewportInfo,
    innerTableRef,
    tableId,
    layout,
    columnsArray,
    stickyColumnInfo,
    columnBorderEdges,
    borderOffset,
    columnWidths,
    appliedColumnWidths,
    stickyEdgesState,
    isRtl,
    _updateStickyEdgesFromResize
  ]);

  // ensure resize observers have access to the latest state information
  const handleResizeCallbackRef = useRef<() => void>(handleResizeCallback);
  handleResizeCallbackRef.current = handleResizeCallback;

  // resize observer callback functions are static to avoid hook re-subscribing each render
  const staticHandleScrollerResize = useCallback(
    (entry: ResizeObserverEntry) => {
      // only queue up an animation frame if there isn't one already pending
      if (
        pendingResizeEntriesRef.current.scroller == null ||
        pendingResizeEntriesRef.current.innerTable == null
      ) {
        onResizeScrollerBusyStart();
        window.requestAnimationFrame(() => {
          handleResizeCallbackRef.current();
          onResizeScrollerBusyEnd();
        });
      }
      pendingResizeEntriesRef.current.scroller = entry;
    },
    [onResizeScrollerBusyStart, onResizeScrollerBusyEnd]
  );
  const staticHandleInnerTableResize = useCallback(
    (entry: ResizeObserverEntry) => {
      // only queue up an animation frame if there isn't one already pending
      if (
        pendingResizeEntriesRef.current.scroller == null ||
        pendingResizeEntriesRef.current.innerTable == null
      ) {
        onResizeTableBusyStart();
        window.requestAnimationFrame(() => {
          handleResizeCallbackRef.current();
          onResizeTableBusyEnd();
        });
      }
      pendingResizeEntriesRef.current.innerTable = entry;
    },
    [onResizeTableBusyStart, onResizeTableBusyEnd]
  );
  useResizeObserver(viewportInfo.viewportConfig.scrollerRef, staticHandleScrollerResize);
  useResizeObserver(innerTableRef, staticHandleInnerTableResize);

  return {
    columnBorderEdges,
    isLayoutRequired,
    viewportInfo,
    columnsArray,
    appliedColumnWidths,
    sizingInfoRef,
    innerTableHeight,
    hasVerticalUnderflow,
    stickyEdgesState,
    setStickyEdgesState,
    stickyColumnInfo,
    overflowInfoRef,
    scrollerRefCallback
  };
}
